import { QueryClient } from "@tanstack/react-query";
import {
	ListeningTrackingService,
	DailyLimitExceededError,
} from "../infrastructure/ListeningTrackingService";
import { FavoriteTrackRepository } from "../infrastructure/FavoriteTrackRepository";
import { AudioManager } from "./AudioManager";
import { AuthManager } from "./AuthManager";
import { BetterAuthManager } from "./BetterAuthManager";
import { PaymentManager } from "./PaymentManager";
import { ApiClient } from "@/lib/__shared__/apiClient";
import betterAuthClient from "@/lib/__shared__/better-auth/client";
import supabase from "@/lib/__shared__/supabase/client";
import { betterAuthStore } from "./stores/betterAuthStore";
import {
	appStore,
	useAppStore,
	type AppStoreProps,
	type AppStore,
	type ModalIds,
} from "./stores/appStore";
import type { PlaybackStore } from "./stores/playbackStore";
import {
	type MultiTimerStore,
	type Timer,
	type TimerStore,
	timerStore,
} from "./stores/timerStore";
import { posthog } from "posthog-js";
import {
	type IRewardsService,
	RewardsService,
} from "../infrastructure/RewardsService";
import {
	type IAttributionService,
	AttributionService,
} from "../infrastructure/AttributionService";
import type { AttributionTouch } from "@coffitivity/shared";

export interface BonusTimeStatus {
	isInBonusTime: boolean;
	bonusTimeRemaining: number;
}

/**
 * AppManager is a singleton class that manages the application state and
 * interactions between different stores. Its main responsibility is provide
 * access to the underlying managers and to orchestrate the interactions between
 * them.
 */
export class AppManager {
	private static instance: AppManager | null = null;
	// daily-free-limit
	private signupTimerId = "SIGNUP_TIMER";
	private dailyLimitTimerId = "DAILY_LIMIT_TIMER";
	private signupTimerMaxSeconds = 30;
	private dailyLimitMaxSeconds = 60 * 60; // 1 hour in seconds

	private appStore: AppStore;
	private playbackStore: PlaybackStore;
	private rewardsService: IRewardsService;
	private attributionService: IAttributionService;
	private timerStore: TimerStore;
	private listeningService: ListeningTrackingService;
	private currentSessionId: string | null = null;

	featureFlags: Record<string, unknown>;
	audioManager: AudioManager;
	authManager = AuthManager;
	betterAuthManager: BetterAuthManager;
	favoriteTrackRepository: FavoriteTrackRepository;
	paymentManager: PaymentManager;
	queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				retry: 1,
			},
		},
	});

	useStore: () => AppStoreProps;
	useTimerStore: TimerStore;

	/**
	 * Derives bonus time status from the daily limit timer.
	 * Returns whether the user is currently consuming bonus reward time
	 * and how many bonus seconds remain.
	 */
	getBonusTimeStatus = (timer: Timer | undefined): BonusTimeStatus => {
		if (!timer) return { isInBonusTime: false, bonusTimeRemaining: 0 };

		const isSubscribed = this.appStore.getState().isUserSubscribed;
		const isLoggedIn = this.appStore.getState().session !== null;

		const isInBonusTime =
			!isSubscribed &&
			isLoggedIn &&
			timer.duration > this.dailyLimitMaxSeconds &&
			timer.elapsedTime >= this.dailyLimitMaxSeconds &&
			timer.remainingTime > 0;

		return {
			isInBonusTime,
			bonusTimeRemaining: isInBonusTime ? timer.remainingTime : 0,
		};
	};

	readonly dailyLimitTimerSelector = (s: MultiTimerStore) =>
		s.timers[this.dailyLimitTimerId];

	static getInstance(): AppManager {
		if (!AppManager.instance) {
			AppManager.instance = new AppManager();
		}
		return AppManager.instance;
	}

	// daily-free-limit
	private createSignupTimer = () => {
		this.timerStore
			.getState()
			.createTimer(this.signupTimerId, this.signupTimerMaxSeconds);
	};

	private createDailyLimitTimer = async () => {
		const session = this.appStore.getState().session;
		const isLoggedIn = session !== null;

		// Always create the timer with default values first
		this.timerStore
			.getState()
			.createTimer(this.dailyLimitTimerId, this.dailyLimitMaxSeconds);

		if (isLoggedIn) {
			try {
				const usage = await this.listeningService.getDailyUsage();

				this.syncTimerWithBonusTime(usage.bonusSecondsRemaining);
				this.timerStore
					.getState()
					.setElapsedTime(this.dailyLimitTimerId, usage.secondsUsedToday);
			} catch (_) {
				// Timer already created with defaults, nothing to do
			}
		}
	};

	recordReferral = async (
		code: string,
		fingerprint: string,
		landingPage: string,
		userAgent: string,
	) => {
		return this.rewardsService.recordReferral(
			code,
			fingerprint,
			landingPage,
			userAgent,
		);
	};

	recordAttributionTouch = async (touch: AttributionTouch) => {
		return this.attributionService.recordTouch(touch);
	};

	getRewards = async () => {
		return this.rewardsService.getRewards();
	};

	markRewardsSeen = async (rewardIds: string[]) => {
		return this.rewardsService.markSeen(rewardIds);
	};

	hasUnseenRewards = async () => {
		return this.rewardsService.hasUnseen();
	};

	resetSignupTimer = () => {
		this.timerStore.getState().resetTimer(this.signupTimerId);
	};

	/**
	 * Extends the daily limit timer's duration to include bonus reward time.
	 * The timer will countdown through dailyLimit + bonus before expiring.
	 */
	private syncTimerWithBonusTime = (bonusSecondsRemaining: number | null) => {
		const bonus = bonusSecondsRemaining ?? 0;
		const totalDuration = this.dailyLimitMaxSeconds + bonus;
		this.timerStore
			.getState()
			.setDuration(this.dailyLimitTimerId, totalDuration);
	};

	// setup playback triggers
	private playbackStateListener = () => {
		const handlePlaybackStatusChange = (status: string) => {
			const session = this.appStore.getState().session;
			const isLoggedIn = session !== null;
			const isSubscribed = this.appStore.getState().isUserSubscribed;

			if (status === "PLAYING") {
				if (!isLoggedIn) {
					// Non-logged-in users get the signup timer only
					this.timerStore.getState().startTimer(this.signupTimerId);
					return;
				}

				// Start the timer immediately so there's no gap in the countdown
				if (!isSubscribed) {
					this.timerStore.getState().startTimer(this.dailyLimitTimerId);
				}

				// Fire the API call without blocking — handle result asynchronously
				const trackId =
					this.playbackStore.getState().currentTrack?.id ?? "unknown";

				this.listeningService
					.startSession(trackId)
					.then((result) => {
						this.currentSessionId = result.sessionId;

						// Sync timer with server-side usage (corrects any drift)
						if (!isSubscribed) {
							this.syncTimerWithBonusTime(result.bonusSecondsRemaining);
							this.timerStore
								.getState()
								.setElapsedTime(
									this.dailyLimitTimerId,
									result.secondsUsedToday,
								);
						}
					})
					.catch(async (error) => {
						if (error instanceof DailyLimitExceededError) {
							this.appStore
								.getState()
								.showModal("EXPIRED_LISTENING_MODAL", true);
							await this.audioManager.pauseTrack();
							return;
						}
						console.error("Failed to start listening session:", error);
					});
			}

			if (status === "PAUSED") {
				if (!isLoggedIn) {
					// we'll give the user a full timer length even after pause since it's so short
					// because they're not logged in.
					this.timerStore.getState().resetTimer(this.signupTimerId);
					return;
				}

				if (!isSubscribed) {
					this.timerStore.getState().pauseTimer(this.dailyLimitTimerId);
				}

				// End the session without blocking — the serializer handles ordering
				if (this.currentSessionId) {
					const sessionId = this.currentSessionId;
					this.currentSessionId = null;

					this.listeningService.endSession(sessionId).catch((error) => {
						console.error("Failed to end listening session:", error);
					});
				}
			}
		};

		// Subscribe to the playback store for only status changes
		this.playbackStore.subscribe(
			(state) => state.status,
			handlePlaybackStatusChange,
		);
	};

	private signupTimerListener = async () => {
		this.timerStore.subscribe(
			(state) => state.timers[this.signupTimerId].remainingTime,
			async (remainingTime) => {
				const isLoggedIn = this.appStore.getState().session;

				// Prevent triggering the signup modal for logged in users
				if (isLoggedIn) return;

				const playback = this.playbackStore.getState();

				if (remainingTime <= 0 && playback.status === "PLAYING") {
					await this.audioManager.pauseTrack();
					this.appStore.getState().showModal("SIGNUP_MODAL", true);
				}
			},
		);
	};

	private syncDailyLimitTimerWithApi = async () => {
		const session = this.appStore.getState().session;
		const isLoggedIn = session !== null;

		if (isLoggedIn) {
			try {
				const usage = await this.listeningService.getDailyUsage();

				const existingTimer =
					this.timerStore.getState().timers[this.dailyLimitTimerId];
				if (existingTimer) {
					this.syncTimerWithBonusTime(usage.bonusSecondsRemaining);
					this.timerStore
						.getState()
						.setElapsedTime(this.dailyLimitTimerId, usage.secondsUsedToday);
				}
			} catch (error) {
				console.error("Failed to sync timer with API:", error);
			}
		} else {
			// Reset timer for logged out users
			this.timerStore.getState().resetTimer(this.dailyLimitTimerId);
		}
	};

	private sessionListener = () => {
		// Subscribe to session changes
		this.appStore.subscribe(
			(state) => state.session,
			async (session, previousSession) => {
				const wasLoggedIn = previousSession !== null;
				const isLoggedIn = session !== null;

				// User just logged in
				if (!wasLoggedIn && isLoggedIn) {
					await this.syncDailyLimitTimerWithApi();
				}

				// User just logged out
				if (wasLoggedIn && !isLoggedIn) {
					// Reset all timers on logout
					this.timerStore.getState().resetTimer(this.dailyLimitTimerId);
					this.timerStore.getState().resetTimer(this.signupTimerId);
				}
			},
		);
	};

	private dailyLimitTimerListener = async () => {
		this.timerStore.subscribe(
			(state) => state.timers[this.dailyLimitTimerId].remainingTime,
			async (remainingTime) => {
				const isSubscribed = this.appStore.getState().isUserSubscribed;

				// Prevent triggering the expired modal for subscribed users
				if (isSubscribed) return;

				const playback = this.playbackStore.getState();

				if (remainingTime <= 0 && playback.status === "PLAYING") {
					await this.audioManager.pauseTrack();
					this.appStore.getState().showModal("EXPIRED_LISTENING_MODAL", true);
				}
			},
		);

		window.onbeforeunload = () => {
			if (this.currentSessionId) {
				this.listeningService.endSessionOnUnload(this.currentSessionId);
			}
		};
	};

	private resetTimers = async () => {
		// daily time limit
		this.createSignupTimer();
		await this.createDailyLimitTimer();
		this.signupTimerListener();
		this.dailyLimitTimerListener();

		// Listen for session changes
		this.sessionListener();
	};

	private constructor() {
		const apiClient = new ApiClient(
			async () => {
				const { data } = await supabase.auth.getSession();
				return data.session?.access_token;
			},
			async () => {
				const { data } = await supabase.auth.refreshSession();
				return data.session?.access_token;
			},
		);

		this.listeningService = new ListeningTrackingService(apiClient);
		this.rewardsService = new RewardsService(apiClient);
		this.attributionService = new AttributionService(apiClient);
		this.favoriteTrackRepository = new FavoriteTrackRepository(apiClient);
		this.paymentManager = new PaymentManager(apiClient);
		this.audioManager = AudioManager.getInstance({
			appStore,
			showModalHandler: this.showModal,
			apiClient,
		});
		this.appStore = appStore;
		this.useStore = useAppStore;
		this.useTimerStore = timerStore;
		this.playbackStore = this.audioManager.stateStore;
		this.timerStore = timerStore;
		this.featureFlags = {};

		posthog.onFeatureFlags(() => {
			// feature flags should be available at this point
			// if (posthog.isFeatureEnabled("custom-blends")) {
			// 	this.featureFlags.customBlends = true;
			// }
		});

		// Initialize timers asynchronously
		this.resetTimers();

		AuthManager.initialize({ queryClient: this.queryClient });

		this.betterAuthManager = new BetterAuthManager(
			betterAuthClient,
			betterAuthStore,
		);
		// Fire-and-forget; mirrors AuthManager.initialize behavior
		this.betterAuthManager.initialize();

		this.playbackStateListener();
	}

	showModal = (id: ModalIds, isVisible: boolean) => {
		this.appStore.getState().showModal(id, isVisible);
	};

	// Public method to manually sync timer with API data - may not need
	syncTimerWithProfile = async () => {
		await this.syncDailyLimitTimerWithApi();
	};
}

export type AppManagerType = AppManager;
